/**
 * User-side announcement operations.
 *
 * The admin panel writes notifications to `/announcements/{id}`. Every
 * signed-in user reads the active ones and the modal is shown for the first
 * one they have not yet dismissed. Dismissals are recorded under
 * `/users/{uid}/seenAnnouncements/{announcementId}` so the modal only ever
 * appears once per (user, announcement) pair, across devices.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { AnnouncementWithId } from "@amazing-wedding/shared-types";

export interface UserNotification {
  id: string;
  title: string;
  body: string;
  kind?: string;
  seenAt: unknown | null;
  createdAt?: unknown;
}

export async function listActiveAnnouncements(): Promise<AnnouncementWithId[]> {
  const snap = await getDocs(
    query(
      collection(db, "announcements"),
      where("active", "==", true),
      orderBy("createdAt", "desc"),
    ),
  );
  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as Omit<AnnouncementWithId, "id">),
  }));
}

/**
 * Returns which of the given announcement IDs the user has already dismissed.
 * Reads only the matching `seenAnnouncements` docs (one `getDoc` each, in
 * parallel) so the cost stays proportional to the small number of currently
 * active announcements rather than to the user's full dismissal history.
 */
export async function getSeenAnnouncementIds(
  userId: string,
  announcementIds: string[],
): Promise<Set<string>> {
  if (announcementIds.length === 0) return new Set();
  const snaps = await Promise.all(
    announcementIds.map((id) => getDoc(doc(db, "users", userId, "seenAnnouncements", id))),
  );
  return new Set(snaps.filter((s) => s.exists()).map((s) => s.id));
}

export async function markAnnouncementSeen(userId: string, announcementId: string): Promise<void> {
  await setDoc(doc(db, "users", userId, "seenAnnouncements", announcementId), {
    seenAt: serverTimestamp(),
  });
}

/**
 * Personal admin-authored notifications stored at /users/{uid}/notifications.
 * These are targeted (e.g. "your plan was upgraded to Premium") and dismiss
 * by stamping `seenAt` on the doc itself rather than via a sibling collection.
 */
export async function listUnseenUserNotifications(userId: string): Promise<UserNotification[]> {
  const snap = await getDocs(
    query(
      collection(db, "users", userId, "notifications"),
      where("seenAt", "==", null),
      orderBy("createdAt", "desc"),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<UserNotification, "id">) }));
}

export async function markUserNotificationSeen(
  userId: string,
  notificationId: string,
): Promise<void> {
  await updateDoc(doc(db, "users", userId, "notifications", notificationId), {
    seenAt: serverTimestamp(),
  });
}
